import { JobStatuses } from "@legacy/core/utils/enums"
import { useMemo, useState } from "react"

import useHasAccess from "@hooks/useHasAccess"
import useSlugExtractor from "@hooks/useSlugExtractor"

import getJobStatusOrSubStatusLabel from "@utils/getJobStatusOrSubStatusLabel"

import { usePreviewMode } from "@particles/PreviewMode/PreviewMode.context"

import { ObjectsView, Table, ViewBar, ViewMenu } from "@organisms"
import TableView from "@organisms/ObjectsView/TableView/TableView"
import ViewTypesProvider from "@organisms/ObjectsView/ViewTypesProvider/ViewTypesProvider"

import JobPreviewCard from "@pages/Jobs/JobList/views/JobTimelineView/JobPreviewCard/JobPreviewCard"

import { JOBS_ROUTES } from "@routes/jobs"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"
import {
    JOB_SERIES_JOBS_TABLE_ACTIVE_TAB,
    JOB_SERIES_JOBS_TABLE_KEYWORD_SEARCH,
    JOB_SERIES_JOBS_TABLE_PHASE,
    JOB_SERIES_JOBS_TABLE_VIEW,
} from "@constants/storage"

import styles from "./JobSeriesJobsTable.module.scss"

interface JobSeriesJobsTableProps {
    jobSeriesId: string
}

export default function JobSeriesJobsTable(props: JobSeriesJobsTableProps) {
    const { jobSeriesId } = props

    const [createRoute] = useSlugExtractor([JOBS_ROUTES.CREATE])
    const { hasPermission } = useHasAccess()

    const seriesFilter = useMemo(() => {
        const params = new URLSearchParams()
        params.set("series", jobSeriesId)
        return params
    }, [jobSeriesId])

    const [hoveredJobId, setHoveredJobId] = useState<Job["id"]>("")
    const { isPreviewModeEnabled } = usePreviewMode()

    return (
        <ViewTypesProvider viewTypes={["table"]} viewTypeKey={JOB_SERIES_JOBS_TABLE_VIEW} defaultViewType="table">
            {() => (
                <div className={styles.base} onMouseLeave={() => setHoveredJobId("")}>
                    <ObjectsView.Provider
                        activePhaseStorageKey={JOB_SERIES_JOBS_TABLE_PHASE}
                        activeTabStorageKey={JOB_SERIES_JOBS_TABLE_ACTIVE_TAB}
                        searchKeywordsStorageKey={JOB_SERIES_JOBS_TABLE_KEYWORD_SEARCH}
                        listTitle="Jobs"
                        listDataFilters={seriesFilter}
                        objectType="job"
                        objectTypeVariation="job_series"
                        objectCreateRoute={createRoute}
                        objectIndexName="Job"
                        objectName="Job"
                        defaultActivePhase="open"
                        isRowClickable={hasPermission("jobs_view_permission", PERMISSION_LEVEL.FULL)}
                        isEmptyStateCreateButtonVisible={false}
                        onRowHoverChange={setHoveredJobId}
                    >
                        <TableView.Provider>
                            <div onMouseEnter={() => setHoveredJobId("")}>
                                <ViewBar.Root>
                                    <ViewBar.Start>
                                        <ViewBar.Phases>
                                            <ObjectsView.Phase phase="open" title="Open" />
                                            <ObjectsView.Phase phase="closed" title="Closed" />
                                        </ViewBar.Phases>
                                        <ViewBar.VerticalDivider />
                                        <ObjectsView.Tabs>
                                            <ObjectsView.Tab
                                                id="all_open"
                                                phase="open"
                                                title="All Open"
                                                isDefaultActiveTabForPhase={true}
                                                endpointKwargs={[
                                                    ["status", JobStatuses.pending],
                                                    ["status", JobStatuses.scheduled],
                                                    ["status", JobStatuses.active],
                                                    ["status", JobStatuses.paused],
                                                    ["status", JobStatuses.on_hold],
                                                    ["status", JobStatuses.completed],
                                                    ["is_archived", "false"],
                                                    ["ordering", "start_time"],
                                                ]}
                                                listEndpoint={JOBS_ROUTES.LIGHTWEIGHT_LIST}
                                                countEndpoint={JOBS_ROUTES.COUNT}
                                            />
                                            <ObjectsView.Tab
                                                id="pending"
                                                title="Pending"
                                                phase="open"
                                                endpointKwargs={[
                                                    ["status", JobStatuses.pending],
                                                    ["is_archived", "false"],
                                                    ["ordering", "-id"],
                                                ]}
                                                listEndpoint={JOBS_ROUTES.LIGHTWEIGHT_LIST}
                                                countEndpoint={JOBS_ROUTES.COUNT}
                                            />
                                            <ObjectsView.Tab
                                                id="scheduled"
                                                title="Scheduled"
                                                phase="open"
                                                endpointKwargs={[
                                                    ["status", JobStatuses.scheduled],
                                                    ["is_archived", "false"],
                                                    ["ordering", "start_time"],
                                                ]}
                                                listEndpoint={JOBS_ROUTES.LIGHTWEIGHT_LIST}
                                                countEndpoint={JOBS_ROUTES.COUNT}
                                            />
                                            <ObjectsView.Tab
                                                id="in_progress"
                                                title="In Progress"
                                                phase="open"
                                                endpointKwargs={[
                                                    ["status", JobStatuses.active],
                                                    ["status", JobStatuses.paused],
                                                    ["is_archived", "false"],
                                                    ["ordering", "start_time"],
                                                ]}
                                                listEndpoint={JOBS_ROUTES.LIGHTWEIGHT_LIST}
                                                countEndpoint={JOBS_ROUTES.COUNT}
                                            />
                                            <ObjectsView.Tab
                                                id="on_hold"
                                                title="On Hold"
                                                phase="open"
                                                endpointKwargs={[
                                                    ["status", JobStatuses.on_hold],
                                                    ["is_archived", "false"],
                                                    ["ordering", "start_time"],
                                                ]}
                                                listEndpoint={JOBS_ROUTES.LIGHTWEIGHT_LIST}
                                                countEndpoint={JOBS_ROUTES.COUNT}
                                            />
                                            <ObjectsView.Tab
                                                id="completed"
                                                title="Completed"
                                                phase="open"
                                                endpointKwargs={[
                                                    ["status", JobStatuses.completed],
                                                    ["is_archived", "false"],
                                                    ["ordering", "-last_completed_at"],
                                                ]}
                                                listEndpoint={JOBS_ROUTES.LIGHTWEIGHT_LIST}
                                                countEndpoint={JOBS_ROUTES.COUNT}
                                            />
                                            <ObjectsView.Tab
                                                id="all_closed"
                                                title="All Closed"
                                                phase="closed"
                                                isDefaultActiveTabForPhase={true}
                                                endpointKwargs={[
                                                    ["status", JobStatuses.finalized],
                                                    ["status", JobStatuses.cancelled],
                                                    ["is_archived", "true"],
                                                    ["ordering", "-id"],
                                                ]}
                                                listEndpoint={JOBS_ROUTES.LIGHTWEIGHT_LIST}
                                                countEndpoint={JOBS_ROUTES.COUNT}
                                            />
                                            <ObjectsView.Tab
                                                id="finalized"
                                                title="Finalized"
                                                phase="closed"
                                                endpointKwargs={[
                                                    ["status", JobStatuses.finalized],
                                                    ["is_archived", "true"],
                                                    ["ordering", "-last_finalized_at"],
                                                ]}
                                                listEndpoint={JOBS_ROUTES.LIGHTWEIGHT_LIST}
                                                countEndpoint={JOBS_ROUTES.COUNT}
                                            />
                                            <ObjectsView.Tab
                                                id="cancelled"
                                                title="Cancelled"
                                                phase="closed"
                                                endpointKwargs={[
                                                    ["status", JobStatuses.cancelled],
                                                    ["is_archived", "true"],
                                                    ["ordering", "-start_time"],
                                                ]}
                                                listEndpoint={JOBS_ROUTES.LIGHTWEIGHT_LIST}
                                                countEndpoint={JOBS_ROUTES.COUNT}
                                            />
                                        </ObjectsView.Tabs>
                                    </ViewBar.Start>
                                    <ViewBar.End>
                                        <ObjectsView.SearchInput />
                                        <ViewMenu />
                                    </ViewBar.End>
                                </ViewBar.Root>
                            </div>

                            <TableView.Table>
                                <Table.Column<Job>
                                    accessorKey="id"
                                    header="ID"
                                    size={144}
                                    cell={({ row }) => (
                                        <Table.Cells.IDCell id={row.original.custom_id || row.original.id} />
                                    )}
                                    enableSorting={true}
                                    enablePinning={true}
                                    dataType="alphabetical"
                                />
                                <Table.Column<Job>
                                    accessorKey="status_label"
                                    header="Status"
                                    size={240}
                                    cell={({ row }) => (
                                        <Table.Cells.StatusCell
                                            objectType="job"
                                            status={row.original.status_label}
                                            statusLabelOverride={getJobStatusOrSubStatusLabel(row.original)}
                                            statusColorOverride={row.original.status_color_override}
                                        />
                                    )}
                                    enableSorting={true}
                                    enablePinning={false}
                                    dataType="other"
                                    loadingType="badge"
                                />
                                <Table.Column<Job>
                                    accessorKey="job_type"
                                    header="Job Type"
                                    size={128}
                                    cell={({ getValue }) => <Table.Cells.TextCell content={getValue()} />}
                                    enableSorting={false}
                                    enablePinning={false}
                                    dataType="other"
                                />
                                <Table.Column<Job>
                                    accessorKey="priority"
                                    header="Priority"
                                    size={144}
                                    cell={({ getValue }) => <Table.Cells.PriorityCell priority={getValue()} />}
                                    enableSorting={true}
                                    enablePinning={true}
                                    dataType="other"
                                />
                                <Table.Column<Job>
                                    accessorKey="assigned_technicians"
                                    header="Techs"
                                    size={80}
                                    cell={({ getValue }) => <Table.Cells.UserAvatarCell users={getValue()} />}
                                    enableSorting={false}
                                    enablePinning={false}
                                    dataType="other"
                                    loadingType="avatar"
                                />
                                <Table.Column<Job>
                                    accessorKey="labels"
                                    header="Labels"
                                    size={240}
                                    cell={({ getValue }) => (
                                        <Table.Cells.BadgesCell
                                            badges={(getValue() as JobLabel[])?.map((label: JobLabel) => label.name)}
                                        />
                                    )}
                                    enableSorting={false}
                                    enablePinning={false}
                                    dataType="other"
                                    loadingType="badge"
                                />
                                <Table.Column<Job>
                                    accessorKey="date_received"
                                    header="Date Created"
                                    size={144}
                                    cell={({ getValue }) => <Table.Cells.DateTimeCell value={getValue()} />}
                                    enableSorting={true}
                                    enablePinning={false}
                                    dataType="timing"
                                />
                                <Table.Column<Job>
                                    accessorKey="start_time"
                                    header="Scheduled For"
                                    size={224}
                                    cell={({ getValue, row }) => (
                                        <Table.Cells.DateTimeCell
                                            value={getValue()}
                                            showTime={true}
                                            isDangerous={
                                                ["Pending", "Scheduled"].includes(row.original.state_label) &&
                                                row.original.is_past_due
                                            }
                                        />
                                    )}
                                    enableSorting={true}
                                    enablePinning={false}
                                    dataType="timing"
                                />
                                <Table.Column<Job>
                                    accessorKey="duration"
                                    header="Duration"
                                    size={112}
                                    cell={({ getValue }) => (
                                        <Table.Cells.NumericalDataCell type="duration" value={getValue()} />
                                    )}
                                    enableSorting={true}
                                    enablePinning={false}
                                    dataType="numerical"
                                    alignment="right"
                                />
                                <Table.Column<Job>
                                    accessorKey="last_status_transition_time"
                                    header="Status Changed"
                                    size={160}
                                    cell={({ getValue }) => <Table.Cells.DateTimeCell value={getValue()} />}
                                    enableSorting={false}
                                    enablePinning={false}
                                    dataType="timing"
                                />
                                <Table.Column<Job>
                                    accessorKey="last_modified_at"
                                    header="Last Modified"
                                    size={144}
                                    cell={({ getValue }) => <Table.Cells.DateTimeCell value={getValue()} />}
                                    enableSorting={true}
                                    enablePinning={false}
                                    dataType="timing"
                                />
                            </TableView.Table>
                        </TableView.Provider>
                    </ObjectsView.Provider>
                    <JobPreviewCard
                        jobId={hoveredJobId || ""}
                        isVisible={!!hoveredJobId && isPreviewModeEnabled}
                        isPositionFixed={true}
                    />
                </div>
            )}
        </ViewTypesProvider>
    )
}
